import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { NotificationList } from '~/components';
import { RenderFields } from '~/components/RenderFields';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';

interface AccountNumberModalProps {
  accountNumber?: AccountNumber;
  bankAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: AccountNumber) => void;
}

interface AccountNumberForm {
  accountDescription: string;
}

const ModalNotificationDisplay = 'account-number-modal';

export const AccountNumberModal = ({
  accountNumber,
  bankAccountId,
  isOpen,
  onClose: onCloseProp,
  onSuccess,
}: AccountNumberModalProps) => {
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: ModalNotificationDisplay });
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  // Preserve edit mode state during transition to prevent flash
  const [isEditMode, setIsEditMode] = useState(Boolean(accountNumber));

  useEffect(() => {
    if (isOpen) {
      setIsEditMode(Boolean(accountNumber));
    }
  }, [isOpen, accountNumber]);

  const formMethods = useForm<AccountNumberForm>({
    defaultValues: {
      accountDescription: accountNumber?.description ?? '',
    },
  });

  const defaultValues = useMemo<AccountNumberForm>(
    () => ({
      accountDescription: accountNumber?.description ?? '',
    }),
    [accountNumber]
  );

  useEffect(() => {
    if (isOpen) {
      formMethods.reset(defaultValues);
    }
  }, [defaultValues, formMethods, isOpen]);

  const handleClose = () => {
    onCloseProp();
  };

  const accountNumberMutation = useMutation({
    mutationFn: (data: AccountNumberForm) => {
      const payload = { description: data.accountDescription || undefined };

      if (accountNumber) {
        return BankAccountRepository.updateAccountNumber(accountNumber.id, payload);
      }

      return BankAccountRepository.createAccountNumber(bankAccountId, payload);
    },
    onSuccess: (data) => {
      addSuccessNotification({
        content: isEditMode ? 'Account number updated' : 'Account number created',
      });
      onSuccess(data);
      handleClose();
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to save account number',
      });
    },
  });

  const submitLabel = isEditMode ? 'Save' : 'Create';
  const modalTitle = isEditMode ? 'Edit Account Number' : 'Create Account Number';

  const onSubmit = formMethods.handleSubmit(
    (data) => accountNumberMutation.mutate(data),
    (errors) => handleSubmitErrors(errors)
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={ModalSize.Small}>
      <Modal.Header title={modalTitle} />

      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <Modal.Content align="left">
            <NotificationList display={ModalNotificationDisplay} spacing="top" />
            {RenderFields<AccountNumberForm>({
              sections: [
                {
                  variant: 'isCompact',
                  fields: [
                    {
                      id: 'accountDescription',
                      label: 'Description',
                      rules: {
                        required: true,
                      },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                        <Input
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Description"
                          isDisabled={accountNumberMutation.isPending}
                          hasError={isTouched && !!error}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
          </Modal.Content>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} size="small" type="button">
              Cancel
            </Button>
            <Button isLoading={accountNumberMutation.isPending} size="small" type="submit">
              {submitLabel}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};
